import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import {
  fetchInterestedProperties,
  fetchSavedProperties,
  fetchDislikedProperties,
  fetchLikedProperties,
} from '../../state/redux/actions/analyticsAction'

const TotalSaved = ({ interestedData, likedData, dislikedData }) => {
  const dispatch = useDispatch()
  const { interestedProperties, savedProperties, likedProperties, dislikedProperties, loading: analyticsLoading } = useSelector(s => s.analytics)

  useEffect(() => {
    if (
      (!interestedData || interestedData.length === 0) &&
      (!interestedProperties || !Array.isArray(interestedProperties.data) || interestedProperties.data.length === 0)
    ) {
      dispatch(fetchInterestedProperties())
      dispatch(fetchSavedProperties())
      dispatch(fetchLikedProperties())
      dispatch(fetchDislikedProperties())
    }
  }, [dispatch, interestedData, interestedProperties])

  const chartData = useMemo(() => {
    // Prefer thunk-provided data when available (payload may be { success, data: [...] })
    const sourceInterestedData = (interestedProperties && Array.isArray(interestedProperties.data) && interestedProperties.data.length > 0)
      ? interestedProperties.data
      : Array.isArray(interestedData) ? interestedData : []

    const sourceLikedData = (likedProperties && Array.isArray(likedProperties.data) && likedProperties.data.length > 0)
      ? likedProperties.data
      : Array.isArray(likedData) ? likedData : []

    const sourceDislikedData = (dislikedProperties && Array.isArray(dislikedProperties.data) && dislikedProperties.data.length > 0)
      ? dislikedProperties.data
      : Array.isArray(dislikedData) ? dislikedData : []

    const timeSlots = ['7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM']

    const groupByHourWithCounts = (data, countField) => {
      const grouped = {}

      if (!Array.isArray(data)) return grouped

      data.forEach(item => {
        const timestamp = item.lastInteractionAt || item.createdAt || item.updatedAt
        if (!timestamp) return

        const date = new Date(timestamp)
        const hour = date.getHours()

        let timeKey
        if (hour === 0) timeKey = '12 AM'
        else if (hour < 12) timeKey = `${hour} AM`
        else if (hour === 12) timeKey = '12 PM'
        else timeKey = `${hour - 12} PM`

        const count = item[countField] || 0
        grouped[timeKey] = (grouped[timeKey] || 0) + count
      })

      return grouped
    }

    const interestedByHour = groupByHourWithCounts(sourceInterestedData, 'interested_count')
    const dislikedByHour = groupByHourWithCounts(sourceInterestedData, 'dislike_count')
    const viewsByHour = groupByHourWithCounts(sourceInterestedData, 'view_count')
    const likedByHour = groupByHourWithCounts(sourceLikedData, 'likecount')

    return timeSlots.map(time => {
      const interestedCount = interestedByHour[time] || 0
      const dislikedCount = dislikedByHour[time] || 0
      const viewsCount = viewsByHour[time] || 0
      const likedCount = likedByHour[time] || 0

      const average = (interestedCount + viewsCount + dislikedCount + likedCount) / 4

      return {
        time,
        'Interested': interestedCount,
        'Connected': viewsCount,
        'Declined': dislikedCount,
        'Favorites': likedCount,
        'Average': parseFloat(average.toFixed(2)),
      }
    })
  }, [interestedData, likedData, dislikedData, interestedProperties, likedProperties, dislikedProperties])

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('TotalSaved chartData:', chartData)
  }

  // Helper function to get nice round numbers for axis
  const getNiceNumber = value => {
    const exponent = Math.floor(Math.log10(value))
    const fraction = value / Math.pow(10, exponent)

    let niceFraction
    if (fraction <= 1) niceFraction = 1
    else if (fraction <= 2) niceFraction = 2
    else if (fraction <= 5) niceFraction = 5
    else niceFraction = 10

    return niceFraction * Math.pow(10, exponent)
  }

  // Dynamic Y-axis based on data
  const { maxValue, yAxisTicks } = useMemo(() => {
    if (!chartData || chartData.length === 0) return { maxValue: 10, yAxisTicks: [0, 2, 4, 6, 8, 10] }

    const calculatedMax = chartData.reduce(
      (m, r) => Math.max(m, r['Interested'] || 0, r['Connected'] || 0, r['Declined'] || 0, r['Favorites'] || 0, r['Average'] || 0),
      0
    )

    // Calculate appropriate max value and ticks based on data range
    let maxValue = Math.max(calculatedMax, 1) // Ensure at least 1

    // If max value is small, use smaller increments
    if (maxValue <= 10) {
      maxValue = Math.ceil(maxValue) // Round up to nearest integer
      const step = Math.ceil(maxValue / 5) || 1
      const ticks = []
      for (let i = 0; i <= maxValue; i += step) {
        ticks.push(i)
      }
      return { maxValue, yAxisTicks: ticks }
    }

    // For larger values, use the original logic but make it flexible
    const roundedMax = Math.ceil(maxValue * 1.1) // Add 10% padding
    const niceMax = getNiceNumber(roundedMax)
    const step = niceMax / 5

    const ticks = []
    for (let i = 0; i <= niceMax; i += step) {
      ticks.push(i)
    }

    return { maxValue: niceMax, yAxisTicks: ticks }
  }, [chartData])

  // Calculate total saved
  const totalSaved = useMemo(() => {
    if (!chartData || chartData.length === 0) return 0
    return chartData.reduce(
      (sum, item) => sum + (item['Interested'] || 0) + (item['Connected'] || 0) + (item['Declined'] || 0) + (item['Favorites'] || 0),
      0
    )
  }, [chartData])

  if (analyticsLoading) {
    return (
      <div className="flex items-center justify-center h-[350px]">
        <div className="text-lg text-gray-600">Loading saved properties data...</div>
      </div>
    )
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[350px]">
        <div className="text-gray-500">No saved properties data available</div>
      </div>
    )
  }

  return (
    <div className="w-full bg-white rounded-lg p-4 h-[400px] flex flex-col">
      {/* Title on top row */}
      <div className="mb-2">
        <div className="text-sm text-gray-600 font-medium">
          Total saved:: <span className="text-md font-bold text-gray-900">{totalSaved.toLocaleString()}</span>
        </div>
      </div>

      <div className="flex-1 w-full h-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 20 }}>
            <defs>
              <linearGradient id="colorInterested" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.0} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="colorConnected" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.0} />
                <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="colorDeclined" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF7300" stopOpacity={0.0} />
                <stop offset="95%" stopColor="#FF7300" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="colorFavorites" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FFC658" stopOpacity={0.0} />
                <stop offset="95%" stopColor="#FFC658" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="colorAverageSaved" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0.4} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="time"
              axisLine={{ stroke: '#000', strokeWidth: 1 }}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              padding={{ left: 10, right: 10 }}
            />

            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={value => {
                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
                if (value >= 1000) return `${(value / 1000).toFixed(0)}k`
                return value
              }}
              domain={[0, maxValue]}
              ticks={yAxisTicks}
              axisLine={true}
              tickLine={true}
              tick={{ fontSize: 11, fill: '#666' }}
              width={45}
              interval={0}
            />

            <Tooltip
              contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px',
                backgroundColor: 'white',
              }}
              labelStyle={{ fontWeight: 'bold', color: '#333', fontSize: '12px' }}
              itemStyle={{ color: '#555', fontSize: '12px' }}
              formatter={(value, name) => [typeof value === 'number' ? value.toLocaleString() : value, name]}
            />

            <Area
              type="linear"
              dataKey="Interested"
              stroke="#8884d8"
              fill="url(#colorInterested)"
              strokeWidth={1}
              dot={false}
            />
            <Area
              type="linear"
              dataKey="Connected"
              stroke="#82ca9d"
              fill="url(#colorConnected)"
              strokeWidth={1}
              dot={false}
            />
            <Area
              type="linear"
              dataKey="Declined"
              stroke="#FF7300"
              fill="url(#colorDeclined)"
              strokeWidth={1}
              dot={false}
            />
            <Area
              type="linear"
              dataKey="Favorites"
              stroke="#FFC658"
              fill="url(#colorFavorites)"
              strokeWidth={1}
              dot={false}
            />
            {/* Average line - styled to match the design */}
            <Area
              type="monotone"
              dataKey="Average"
              stroke="#8884d8"
              fill="url(#colorAverageSaved)"
              strokeWidth={0}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend positioned below the graph */}
      <div className="flex justify-center mt-2">
        <div className="flex items-center space-x-4 text-[10px]">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 rounded-full bg-[#8884d8]"></div>
            <span>Interested</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 rounded-full bg-[#82ca9d]"></div>
            <span>Connected</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 rounded-full bg-[#FF7300]"></div>
            <span>Declined</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 rounded-full bg-[#FFC658]"></div>
            <span>Favorites</span>
          </div>
        </div>
      </div>
    </div>
  )
}

TotalSaved.propTypes = {
  interestedData: PropTypes.array,
  likedData: PropTypes.array,
  dislikedData: PropTypes.array,
}

TotalSaved.defaultProps = {
  interestedData: undefined,
  likedData: undefined,
  dislikedData: undefined,
}

export default TotalSaved
